import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtDate, fmtDT, fmtPKR } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Package, Globe, Bell, BarChart2, MapPin } from 'lucide-react';

// ══════════════════════════════════════════════════════════
// WAREHOUSE / 3PL
// ══════════════════════════════════════════════════════════
export function WarehousePage() {
  const [tab, setTab]           = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showReceive, setShowReceive] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ customerId:'', locationId:'', sku:'', name:'', quantity:'1', unit:'PCS', weightPerUnit:'', trackingId:'', notes:'' });
  const [locationForm, setLocationForm] = useState({ hubId:'', aisle:'A', rack:'01', bin:'001', zoneType:'GENERAL', capacityUnits:'100' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, inv, locs] = await Promise.all([
        api.get('/warehouse/dashboard').then(r=>r.data),
        api.get('/warehouse/inventory?limit=50').then(r=>r.data),
        api.get('/warehouse/locations').then(r=>r.data),
      ]);
      if (d.success)    setDashboard(d.data);
      if (inv.success)  setInventory(inv.data);
      if (locs.success) setLocations(locs.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReceive = async () => {
    if (!receiveForm.customerId||!receiveForm.locationId||!receiveForm.name) { toast.error('Customer, location and item name required'); return; }
    try {
      const r = await api.post('/warehouse/receive', receiveForm).then(r=>r.data);
      if (r.success) { toast.success('Item received'); setShowReceive(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const handleDispatch = async (itemId, availQty) => {
    const qty = prompt(`Dispatch quantity (available: ${availQty}):`);
    if (!qty || isNaN(qty) || parseInt(qty)<=0) return;
    try {
      await api.post('/warehouse/dispatch', { itemId, quantity:parseInt(qty) }).then(r=>r.data);
      toast.success('Dispatched'); load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const createLocation = async () => {
    try {
      const r = await api.post('/warehouse/locations', locationForm).then(r=>r.data);
      if (r.success) { toast.success('Location created'); setShowLocation(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const utilPct = dashboard?.locations ? (parseFloat(dashboard.locations.used_cap||0)/parseFloat(dashboard.locations.total_cap||1)*100).toFixed(0) : 0;

  return (
    <AppShell title="Warehouse Management (3PL)" actions={
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-outline btn-sm" onClick={()=>setShowLocation(true)}><Plus size={13}/> Location</button>
        <button className="btn btn-primary btn-sm" onClick={()=>setShowReceive(true)}><Package size={13}/> Receive Stock</button>
      </div>
    }>
      <div className="tabs">
        {['dashboard','inventory','locations'].map(t=>(
          <div key={t} className={`tab${tab===t?' active':''}`} onClick={()=>setTab(t)} style={{ textTransform:'capitalize' }}>{t}</div>
        ))}
      </div>

      {tab==='dashboard' && dashboard && (
        <>
          <div className="stat-grid" style={{ marginBottom:20 }}>
            <WKpi label="Locations"        v={dashboard.locations?.total||0} />
            <WKpi label="Capacity Used"    v={`${utilPct}%`} color={parseInt(utilPct)>80?'var(--danger)':parseInt(utilPct)>60?'var(--warning)':'var(--success)'} />
            <WKpi label="Total Capacity"   v={dashboard.locations?.total_cap||0} />
            <WKpi label="Items In Storage" v={dashboard.inventory?.find(i=>i.status==='STORED')?.count||0} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Inventory by Status</span></div>
              <div className="card-body">
                {(dashboard.inventory||[]).map(s=>(
                  <div key={s.status} className="flex-between" style={{ padding:'8px 0', borderBottom:'1px solid var(--gray-100)' }}>
                    <span className={`badge ${s.status==='STORED'?'badge-success':s.status==='DISPATCHED'?'badge-navy':'badge-warning'}`}>{s.status}</span>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontWeight:600 }}>{s.count} items</div>
                      <div style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>{s.units} units</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">Movements (7 days)</span></div>
              <div className="card-body">
                {(dashboard.movements||[]).map(m=>(
                  <div key={m.movement_type} className="flex-between" style={{ padding:'8px 0', borderBottom:'1px solid var(--gray-100)' }}>
                    <span style={{ fontSize:'.85rem' }}>{m.movement_type}</span>
                    <strong>{m.count}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {tab==='inventory' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : inventory.length ? (
              <table>
                <thead><tr><th>SKU</th><th>Name</th><th>Customer</th><th>Location</th><th>Qty</th><th>Status</th><th>Received</th><th>Actions</th></tr></thead>
                <tbody>
                  {inventory.map(i=>(
                    <tr key={i.item_id}>
                      <td><code style={{ fontSize:'.75rem' }}>{i.sku||'—'}</code></td>
                      <td style={{ fontWeight:500 }}>{i.name}</td>
                      <td>
                        <div className="text-sm">{i.customer_name}</div>
                        <div className="text-sm text-muted">{i.company_name}</div>
                      </td>
                      <td><code style={{ fontSize:'.75rem' }}>{i.aisle}-{i.rack}-{i.bin}</code></td>
                      <td style={{ fontWeight:600 }}>{i.quantity} {i.unit}</td>
                      <td><span className={`badge ${i.status==='STORED'?'badge-success':i.status==='DISPATCHED'?'badge-navy':'badge-warning'}`}>{i.status}</span></td>
                      <td className="text-sm">{fmtDate(i.received_at)}</td>
                      <td>
                        {i.status==='STORED' && i.quantity>0 && (
                          <button className="btn btn-outline btn-sm" onClick={()=>handleDispatch(i.item_id,i.quantity)}>Dispatch</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="📦" title="No inventory" />}
          </div>
        </div>
      )}

      {tab==='locations' && (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Aisle-Rack-Bin</th><th>Zone</th><th>Hub</th><th>Capacity</th><th>Used</th><th>Utilisation</th><th>Items</th></tr></thead>
              <tbody>
                {locations.map(l=>(
                  <tr key={l.location_id}>
                    <td><code style={{ fontWeight:700 }}>{l.aisle}-{l.rack}-{l.bin}</code></td>
                    <td><span className={`badge ${l.zone_type==='COLD'?'badge-info':l.zone_type==='HAZMAT'?'badge-danger':'badge-gray'}`}>{l.zone_type}</span></td>
                    <td className="text-sm">{l.hub_name||'—'}</td>
                    <td>{l.capacity_units}</td>
                    <td>{l.used_units||0}</td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:60, height:6, background:'var(--gray-200)', borderRadius:3 }}>
                          <div style={{ height:'100%', borderRadius:3, background:'var(--success)', width:`${Math.min(100,(l.used_units||0)/l.capacity_units*100).toFixed(0)}%` }}/>
                        </div>
                        <span style={{ fontSize:'.72rem' }}>{((l.used_units||0)/l.capacity_units*100).toFixed(0)}%</span>
                      </div>
                    </td>
                    <td>{l.item_count||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!locations.length && <EmptyState icon="🏬" title="No warehouse locations configured" />}
          </div>
        </div>
      )}

      {/* Receive modal */}
      <Modal open={showReceive} onClose={()=>setShowReceive(false)} title="Receive Inventory" size="modal-lg">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Customer ID <span className="req">*</span></label><input className="form-control" value={receiveForm.customerId} onChange={e=>setReceiveForm(f=>({...f,customerId:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Location <span className="req">*</span></label>
              <select className="form-control" value={receiveForm.locationId} onChange={e=>setReceiveForm(f=>({...f,locationId:e.target.value}))}>
                <option value="">— Select Location —</option>
                {locations.map(l=><option key={l.location_id} value={l.location_id}>{l.aisle}-{l.rack}-{l.bin} ({l.zone_type})</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">SKU</label><input className="form-control" placeholder="SKU-001" value={receiveForm.sku} onChange={e=>setReceiveForm(f=>({...f,sku:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Item Name <span className="req">*</span></label><input className="form-control" value={receiveForm.name} onChange={e=>setReceiveForm(f=>({...f,name:e.target.value}))}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Quantity <span className="req">*</span></label><input className="form-control" type="number" min="1" value={receiveForm.quantity} onChange={e=>setReceiveForm(f=>({...f,quantity:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Unit</label>
              <select className="form-control" value={receiveForm.unit} onChange={e=>setReceiveForm(f=>({...f,unit:e.target.value}))}>
                {['PCS','KG','BOX','PALLET','ROLL','LTR'].map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Linked Tracking ID</label><input className="form-control" placeholder="ECLPK…" value={receiveForm.trackingId} onChange={e=>setReceiveForm(f=>({...f,trackingId:e.target.value}))}/></div>
          <div className="form-group"><label className="form-label">Notes</label><input className="form-control" value={receiveForm.notes} onChange={e=>setReceiveForm(f=>({...f,notes:e.target.value}))}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowReceive(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleReceive}>Receive</button>
        </div>
      </Modal>

      {/* Add location modal */}
      <Modal open={showLocation} onClose={()=>setShowLocation(false)} title="Add Warehouse Location">
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Hub ID</label><input className="form-control" value={locationForm.hubId} onChange={e=>setLocationForm(f=>({...f,hubId:e.target.value}))}/></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Aisle</label><input className="form-control" placeholder="A" value={locationForm.aisle} onChange={e=>setLocationForm(f=>({...f,aisle:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Rack</label><input className="form-control" placeholder="01" value={locationForm.rack} onChange={e=>setLocationForm(f=>({...f,rack:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Bin</label><input className="form-control" placeholder="001" value={locationForm.bin} onChange={e=>setLocationForm(f=>({...f,bin:e.target.value}))}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Zone Type</label>
              <select className="form-control" value={locationForm.zoneType} onChange={e=>setLocationForm(f=>({...f,zoneType:e.target.value}))}>
                {['GENERAL','COLD','HAZMAT','OVERSIZED','RETURNS'].map(z=><option key={z} value={z}>{z}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Capacity (units)</label><input className="form-control" type="number" value={locationForm.capacityUnits} onChange={e=>setLocationForm(f=>({...f,capacityUnits:e.target.value}))}/></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowLocation(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={createLocation}>Create Location</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════
// INTERNATIONAL SHIPMENTS
// ══════════════════════════════════════════════════════════
export function InternationalPage() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [csFilter, setCsFilter]   = useState('');
  const [selected, setSelected]   = useState(null);
  const [detail, setDetail]       = useState(null);
  const [showAdd, setShowAdd]     = useState(false);
  const [form, setForm] = useState({ trackingId:'', originCountry:'PK', destCountry:'', awbNumber:'', hsCode:'', commodityDesc:'', customsValue:'', dutyAmount:'', airline:'', flightNo:'', etd:'', eta:'', portOfLoading:'KHI', portOfDischarge:'', incoterms:'CIF' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = csFilter ? `?customsStatus=${csFilter}` : '';
      const r = await api.get(`/international${params}`).then(r=>r.data);
      if (r.success) setShipments(r.data);
    } finally { setLoading(false); }
  }, [csFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (s) => {
    setSelected(s);
    const r = await api.get(`/international/${s.tracking_id}`).then(r=>r.data);
    if (r.success) setDetail(r.data);
  };

  const updateCustoms = async (trackingId, status) => {
    const ref = prompt('Customs reference number:');
    try {
      await api.patch(`/international/${trackingId}/customs`, { customsStatus:status, customsRef:ref||undefined });
      toast.success('Customs status updated'); load(); setSelected(null);
    } catch { toast.error('Failed'); }
  };

  const handleAdd = async () => {
    if (!form.trackingId||!form.destCountry) { toast.error('Tracking ID and destination country required'); return; }
    try {
      const r = await api.post('/international', form).then(r=>r.data);
      if (r.success) { toast.success('International record created'); setShowAdd(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const CS_BADGE = { PENDING:'badge-warning', FILED:'badge-info', CLEARED:'badge-success', HELD:'badge-danger', REFUSED:'badge-danger' };

  return (
    <AppShell title="International Shipments" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Globe size={13}/> Add Intl Record</button>
    }>
      <div className="filters-bar">
        <select className="form-control" style={{ width:160 }} value={csFilter} onChange={e=>setCsFilter(e.target.value)}>
          <option value="">All Customs Status</option>
          {['PENDING','FILED','CLEARED','HELD','REFUSED'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : shipments.length ? (
            <table>
              <thead><tr><th>Tracking ID</th><th>Route</th><th>Customer</th><th>Service</th><th>AWB</th><th>Airline/Flight</th><th>ETA</th><th>Customs</th><th></th></tr></thead>
              <tbody>
                {shipments.map(s=>(
                  <tr key={s.tracking_id}>
                    <td><code style={{ fontSize:'.75rem', color:'var(--navy)', fontWeight:700 }}>{s.tracking_id}</code></td>
                    <td style={{ fontSize:'.82rem' }}>{s.origin_country} → {s.dest_country}</td>
                    <td className="text-sm">{s.customer_name}</td>
                    <td><span className="badge badge-navy" style={{ fontSize:'.68rem' }}>{s.service_type}</span></td>
                    <td><code style={{ fontSize:'.72rem' }}>{s.awb_number||'—'}</code></td>
                    <td style={{ fontSize:'.82rem' }}>{s.airline||'—'} {s.flight_no||''}</td>
                    <td className="text-sm">{s.eta ? fmtDate(s.eta) : '—'}</td>
                    <td><span className={`badge ${CS_BADGE[s.customs_status]||'badge-gray'}`}>{s.customs_status}</span></td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={()=>openDetail(s)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="✈️" title="No international shipments" />}
        </div>
      </div>

      {/* Detail modal */}
      <Modal open={!!selected} onClose={()=>{setSelected(null);setDetail(null);}} title={`Intl: ${selected?.tracking_id}`} size="modal-lg">
        {detail ? (
          <>
            <div className="modal-body">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, padding:12, background:'var(--gray-50)', borderRadius:8, marginBottom:16 }}>
                {[
                  ['Route', `${detail.origin_country} → ${detail.dest_country}`],
                  ['AWB', detail.awb_number||'—'],
                  ['MAWB', detail.mawb_number||'—'],
                  ['HS Code', detail.hs_code||'—'],
                  ['Airline', detail.airline||'—'],
                  ['Flight', detail.flight_no||'—'],
                  ['ETD', detail.etd ? fmtDate(detail.etd) : '—'],
                  ['ETA', detail.eta ? fmtDate(detail.eta) : '—'],
                  ['Port of Loading', detail.port_of_loading||'—'],
                  ['Port of Discharge', detail.port_of_discharge||'—'],
                  ['Customs Value', fmtPKR(detail.customs_value)],
                  ['Duty', fmtPKR(detail.duty_amount)],
                  ['Incoterms', detail.incoterms||'—'],
                  ['Customs Ref', detail.customs_ref||'—'],
                ].map(([l,v])=>(
                  <div key={l}>
                    <div style={{ fontSize:'.68rem', color:'var(--gray-400)' }}>{l}</div>
                    <div style={{ fontSize:'.85rem', fontWeight:500 }}>{v}</div>
                  </div>
                ))}
              </div>
              {detail.customs_status !== 'CLEARED' && (
                <div style={{ display:'flex', gap:8 }}>
                  {detail.customs_status==='PENDING'  && <button className="btn btn-primary btn-sm" onClick={()=>updateCustoms(detail.tracking_id,'FILED')}>📄 File Customs</button>}
                  {detail.customs_status==='FILED'    && <button className="btn btn-success btn-sm" onClick={()=>updateCustoms(detail.tracking_id,'CLEARED')}>✅ Mark Cleared</button>}
                  {!['CLEARED','REFUSED'].includes(detail.customs_status) && <button className="btn btn-danger btn-sm" onClick={()=>updateCustoms(detail.tracking_id,'HELD')}>🔒 Hold</button>}
                </div>
              )}
            </div>
          </>
        ) : <div className="loading-center" style={{ height:200 }}><div className="spinner"/></div>}
      </Modal>

      {/* Add modal */}
      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Add International Shipment Record" size="modal-lg">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Tracking ID <span className="req">*</span></label><input className="form-control" value={form.trackingId} onChange={set('trackingId')}/></div>
            <div className="form-row" style={{ margin:0, gap:8 }}>
              <div className="form-group"><label className="form-label">Origin</label><input className="form-control" placeholder="PK" value={form.originCountry} onChange={set('originCountry')}/></div>
              <div className="form-group"><label className="form-label">Dest <span className="req">*</span></label><input className="form-control" placeholder="AE" value={form.destCountry} onChange={set('destCountry')}/></div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">AWB Number</label><input className="form-control" value={form.awbNumber} onChange={set('awbNumber')}/></div>
            <div className="form-group"><label className="form-label">HS Code</label><input className="form-control" placeholder="6204.62" value={form.hsCode} onChange={set('hsCode')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Airline</label><input className="form-control" placeholder="PIA" value={form.airline} onChange={set('airline')}/></div>
            <div className="form-group"><label className="form-label">Flight No.</label><input className="form-control" placeholder="PK-303" value={form.flightNo} onChange={set('flightNo')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">ETD</label><input className="form-control" type="date" value={form.etd} onChange={set('etd')}/></div>
            <div className="form-group"><label className="form-label">ETA</label><input className="form-control" type="date" value={form.eta} onChange={set('eta')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Customs Value (PKR)</label><input className="form-control" type="number" value={form.customsValue} onChange={set('customsValue')}/></div>
            <div className="form-group"><label className="form-label">Duty Amount</label><input className="form-control" type="number" value={form.dutyAmount} onChange={set('dutyAmount')}/></div>
          </div>
          <div className="form-group"><label className="form-label">Commodity Description</label><input className="form-control" value={form.commodityDesc} onChange={set('commodityDesc')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd}><Globe size={13}/> Create Record</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════
// SALES FORCE AUTOMATION
// ══════════════════════════════════════════════════════════
export function SalesPage() {
  const [tab, setTab]         = useState('visits');
  const [visits, setVisits]   = useState([]);
  const [performance, setPerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [month, setMonth]     = useState(new Date().getMonth()+1);
  const [year, setYear]       = useState(new Date().getFullYear());
  const [form, setForm] = useState({ prospectName:'', prospectCompany:'', visitType:'VISIT', outcome:'PROSPECT', notes:'', nextFollowup:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, p] = await Promise.all([
        api.get('/sales/visits?limit=50').then(r=>r.data),
        api.get(`/sales/performance?month=${month}&year=${year}`).then(r=>r.data),
      ]);
      if (v.success) setVisits(v.data);
      if (p.success) setPerf(p.data);
    } finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const addVisit = async () => {
    try {
      const r = await api.post('/sales/visits', form).then(r=>r.data);
      if (r.success) { toast.success('Visit logged'); setShowAdd(false); load(); }
    } catch { toast.error('Failed'); }
  };

  const OUTCOME_BADGE = { PROSPECT:'badge-info', FOLLOW_UP:'badge-warning', CLOSED_WON:'badge-success', CLOSED_LOST:'badge-danger', NOT_INTERESTED:'badge-gray' };
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <AppShell title="Sales Force Automation" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={13}/> Log Visit</button>
    }>
      <div className="tabs">
        <div className={`tab${tab==='visits'?' active':''}`} onClick={()=>setTab('visits')}>Visit Log</div>
        <div className={`tab${tab==='performance'?' active':''}`} onClick={()=>setTab('performance')}>Performance</div>
      </div>

      {tab==='visits' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : visits.length ? (
              <table>
                <thead><tr><th>Sales Rep</th><th>Customer/Prospect</th><th>Type</th><th>Outcome</th><th>Date</th><th>Follow-up</th><th>Notes</th></tr></thead>
                <tbody>
                  {visits.map(v=>(
                    <tr key={v.visit_id}>
                      <td style={{ fontWeight:500 }}>{v.rep_name}</td>
                      <td>
                        <div style={{ fontWeight:500 }}>{v.customer_name||v.prospect_name||'—'}</div>
                        <div className="text-sm text-muted">{v.company_name||v.prospect_company}</div>
                      </td>
                      <td><span className="badge badge-info" style={{ fontSize:'.68rem' }}>{v.visit_type}</span></td>
                      <td><span className={`badge ${OUTCOME_BADGE[v.outcome]||'badge-gray'}`} style={{ fontSize:'.68rem' }}>{v.outcome?.replace(/_/g,' ')}</span></td>
                      <td className="text-sm">{fmtDate(v.checkin_at)}</td>
                      <td className="text-sm">{v.next_followup ? fmtDate(v.next_followup) : '—'}</td>
                      <td style={{ maxWidth:180 }}><span className="truncate text-sm" style={{ display:'block' }}>{v.notes||'—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="🤝" title="No visits logged" />}
          </div>
        </div>
      )}

      {tab==='performance' && (
        <>
          <div className="filters-bar">
            <select className="form-control" style={{ width:120 }} value={month} onChange={e=>setMonth(parseInt(e.target.value))}>
              {months.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
            </select>
            <select className="form-control" style={{ width:100 }} value={year} onChange={e=>setYear(parseInt(e.target.value))}>
              {[2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="card">
            <div className="table-wrap">
              {performance.length ? (
                <table>
                  <thead><tr><th>Rep</th><th>Target Revenue</th><th>Achieved</th><th>Achievement%</th><th>Visits</th><th>Won</th><th>Shipments</th></tr></thead>
                  <tbody>
                    {performance.map(p=>{
                      const achPct = p.target_revenue > 0 ? (parseFloat(p.achieved_revenue)/parseFloat(p.target_revenue)*100).toFixed(0) : 0;
                      return (
                        <tr key={p.emp_id}>
                          <td style={{ fontWeight:500 }}>{p.rep_name}</td>
                          <td>{fmtPKR(p.target_revenue)}</td>
                          <td>{fmtPKR(p.achieved_revenue)}</td>
                          <td>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <div style={{ width:60, height:8, background:'var(--gray-200)', borderRadius:4, overflow:'hidden' }}>
                                <div style={{ height:'100%', borderRadius:4, background: parseInt(achPct)>=100?'var(--success)':parseInt(achPct)>=75?'var(--amber)':'var(--danger)', width:`${Math.min(100,achPct)}%` }}/>
                              </div>
                              <span style={{ fontWeight:600, fontSize:'.85rem', color: parseInt(achPct)>=100?'var(--success)':parseInt(achPct)>=75?'var(--amber)':'var(--danger)' }}>{achPct}%</span>
                            </div>
                          </td>
                          <td>{p.visits_made}</td>
                          <td style={{ color:'var(--success)', fontWeight:500 }}>{p.closed_won}</td>
                          <td>{p.achieved_shipments}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : <EmptyState icon="📊" title="No performance data" />}
            </div>
          </div>
        </>
      )}

      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Log Sales Visit/Activity">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Prospect Name</label><input className="form-control" value={form.prospectName} onChange={set('prospectName')}/></div>
            <div className="form-group"><label className="form-label">Company</label><input className="form-control" value={form.prospectCompany} onChange={set('prospectCompany')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Visit Type</label>
              <select className="form-control" value={form.visitType} onChange={set('visitType')}>
                {['CALL','VISIT','EMAIL','DEMO'].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Outcome</label>
              <select className="form-control" value={form.outcome} onChange={set('outcome')}>
                {['PROSPECT','FOLLOW_UP','CLOSED_WON','CLOSED_LOST','NOT_INTERESTED'].map(o=><option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Follow-up Date</label><input className="form-control" type="date" value={form.nextFollowup} onChange={set('nextFollowup')}/></div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={3} value={form.notes} onChange={set('notes')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={addVisit}><MapPin size={13}/> Log Visit</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════
// NOTIFICATION TEMPLATES
// ══════════════════════════════════════════════════════════
export function NotificationsPage() {
  const [templates, setTemplates] = useState([]);
  const [log, setLog]             = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('templates');
  const [showCreate, setShowCreate] = useState(false);
  const [editTpl, setEditTpl]     = useState(null);
  const [form, setForm] = useState({ name:'', eventType:'BOOKING_CONFIRMED', channel:'SMS', subject:'', body:'', language:'en' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.get('/notifications/templates').then(r=>r.data),
        api.get('/notifications/log?limit=50').then(r=>r.data),
      ]);
      if (t.success) setTemplates(t.data);
      if (l.success) setLog(l.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveTemplate = async () => {
    try {
      if (editTpl) {
        await api.patch(`/notifications/templates/${editTpl.template_id}`, form).then(r=>r.data);
        toast.success('Template updated');
      } else {
        const r = await api.post('/notifications/templates', form).then(r=>r.data);
        if (!r.success) throw new Error(r.message);
        toast.success('Template created');
      }
      setShowCreate(false); setEditTpl(null); load();
    } catch(e) { toast.error(e.message||'Failed'); }
  };

  const toggleActive = async (tpl) => {
    await api.patch(`/notifications/templates/${tpl.template_id}`, { ...tpl, isActive: !tpl.is_active });
    toast.success('Updated'); load();
  };

  const CHANNEL_COLORS = { SMS:'badge-success', EMAIL:'badge-info', PUSH:'badge-warning', WHATSAPP:'badge-navy' };

  return (
    <AppShell title="Notification Templates" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>{setEditTpl(null);setShowCreate(true);}}><Plus size={13}/> New Template</button>
    }>
      <div className="tabs">
        <div className={`tab${tab==='templates'?' active':''}`} onClick={()=>setTab('templates')}>Templates ({templates.length})</div>
        <div className={`tab${tab==='log'?' active':''}`} onClick={()=>setTab('log')}>Send Log</div>
      </div>

      {tab==='templates' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : templates.length ? (
              <table>
                <thead><tr><th>Name</th><th>Event</th><th>Channel</th><th>Language</th><th>Preview</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {templates.map(t=>(
                    <tr key={t.template_id}>
                      <td style={{ fontWeight:600 }}>{t.name}</td>
                      <td><code style={{ fontSize:'.72rem' }}>{t.event_type}</code></td>
                      <td><span className={`badge ${CHANNEL_COLORS[t.channel]||'badge-gray'}`}>{t.channel}</span></td>
                      <td><span className="badge badge-gray">{t.language}</span></td>
                      <td style={{ maxWidth:220 }}><span className="truncate text-sm" style={{ display:'block', color:'var(--gray-500)' }}>{t.body}</span></td>
                      <td><span className={`badge ${t.is_active?'badge-success':'badge-gray'}`}>{t.is_active?'Active':'Inactive'}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-outline btn-sm" onClick={()=>{setEditTpl(t);setForm({name:t.name,eventType:t.event_type,channel:t.channel,subject:t.subject||'',body:t.body,language:t.language});setShowCreate(true);}}>Edit</button>
                          <button className={`btn btn-sm ${t.is_active?'btn-outline':'btn-success'}`} onClick={()=>toggleActive(t)}>{t.is_active?'Disable':'Enable'}</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="📣" title="No notification templates" />}
          </div>
        </div>
      )}

      {tab==='log' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : log.length ? (
              <table>
                <thead><tr><th>Recipient</th><th>Channel</th><th>Event</th><th>Tracking</th><th>Status</th><th>Sent At</th></tr></thead>
                <tbody>
                  {log.map(l=>(
                    <tr key={l.log_id}>
                      <td className="text-sm">{l.recipient_contact}</td>
                      <td><span className={`badge ${CHANNEL_COLORS[l.channel]||'badge-gray'}`}>{l.channel}</span></td>
                      <td className="text-sm">{l.event_type}</td>
                      <td><code style={{ fontSize:'.72rem' }}>{l.tracking_id||'—'}</code></td>
                      <td><span className={`badge ${l.status==='DELIVERED'?'badge-success':l.status==='FAILED'?'badge-danger':'badge-info'}`}>{l.status}</span></td>
                      <td className="text-sm">{fmtDT(l.sent_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="📬" title="No notification logs" />}
          </div>
        </div>
      )}

      <Modal open={showCreate} onClose={()=>{setShowCreate(false);setEditTpl(null);}} title={editTpl?`Edit: ${editTpl.name}`:'New Template'} size="modal-lg">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Template Name <span className="req">*</span></label><input className="form-control" value={form.name} onChange={set('name')} disabled={!!editTpl}/></div>
            <div className="form-group"><label className="form-label">Channel <span className="req">*</span></label>
              <select className="form-control" value={form.channel} onChange={set('channel')}>
                {['SMS','EMAIL','PUSH','WHATSAPP'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Event Type <span className="req">*</span></label>
              <select className="form-control" value={form.eventType} onChange={set('eventType')}>
                {['BOOKING_CONFIRMED','PICKUP_SCHEDULED','PICKED_UP','IN_TRANSIT','OUT_FOR_DELIVERY','DELIVERED','DELIVERY_ATTEMPTED','RTO_INITIATED','TICKET_CREATED'].map(e=><option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Language</label>
              <select className="form-control" value={form.language} onChange={set('language')}>
                {['en','ur','ar','hi'].map(l=><option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          {form.channel==='EMAIL' && (
            <div className="form-group"><label className="form-label">Subject</label><input className="form-control" value={form.subject} onChange={set('subject')}/></div>
          )}
          <div className="form-group">
            <label className="form-label">Message Body <span className="req">*</span><span style={{ fontSize:'.7rem', color:'var(--gray-400)', marginLeft:8 }}>Use {'{tracking_id}'}, {'{customer_name}'}, etc.</span></label>
            <textarea className="form-control" rows={4} value={form.body} onChange={set('body')}/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>{setShowCreate(false);setEditTpl(null);}}>Cancel</button>
          <button className="btn btn-primary" onClick={saveTemplate}><Bell size={13}/> {editTpl?'Save Changes':'Create Template'}</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════
// QSR REPORTS
// ══════════════════════════════════════════════════════════
export function QSRPage() {
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ customerId:'', periodStart:'', periodEnd:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/qsr').then(r=>r.data);
      if (r.success) setReports(r.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!form.customerId||!form.periodStart||!form.periodEnd) { toast.error('All fields required'); return; }
    setGenerating(true);
    try {
      const r = await api.post('/qsr/generate', form).then(r=>r.data);
      if (r.success) { toast.success('QSR generated'); load(); }
    } catch { toast.error('Generation failed'); }
    finally { setGenerating(false); }
  };

  const printQSR = (q) => {
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>QSR ${q.customer_name}</title>
    <style>body{font-family:Arial;font-size:12px;margin:30px} .header{border-bottom:2px solid #1B2A4A;padding-bottom:12px;margin-bottom:20px} .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px} .kpi{border:1px solid #eee;padding:12px;border-radius:4px;text-align:center} .kpi-val{font-size:2rem;font-weight:bold;color:#1B2A4A} .kpi-lbl{color:#666;font-size:11px}</style>
    </head><body>
    <div class="header"><h2 style="margin:0;color:#1B2A4A">Quality Service Report — ${q.customer_name||q.company_name}</h2>
    <div>Period: ${q.period_start} to ${q.period_end}</div></div>
    <div class="grid">
      <div class="kpi"><div class="kpi-val">${q.total_shipments}</div><div class="kpi-lbl">Total Shipments</div></div>
      <div class="kpi"><div class="kpi-val">${q.delivery_rate}%</div><div class="kpi-lbl">Delivery Success Rate</div></div>
      <div class="kpi"><div class="kpi-val">${q.on_time_rate}%</div><div class="kpi-lbl">On-Time Rate</div></div>
      <div class="kpi"><div class="kpi-val">${q.avg_transit_days||'—'}</div><div class="kpi-lbl">Avg Transit Days</div></div>
      <div class="kpi"><div class="kpi-val">${q.rto}</div><div class="kpi-lbl">RTO Shipments</div></div>
      <div class="kpi"><div class="kpi-val">${q.damaged}</div><div class="kpi-lbl">Damaged</div></div>
    </div>
    </body></html>`);
    w.print();
  };

  return (
    <AppShell title="Quality Service Reports (QSR)">
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header"><span className="card-title">Generate QSR for Corporate Customer</span></div>
        <div className="card-body">
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div className="form-group" style={{ margin:0, flex:'1 1 200px' }}>
              <label className="form-label">Customer ID <span className="req">*</span></label>
              <input className="form-control" placeholder="UUID of B2B customer" value={form.customerId} onChange={set('customerId')} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Period From <span className="req">*</span></label>
              <input className="form-control" type="date" value={form.periodStart} onChange={set('periodStart')} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Period To <span className="req">*</span></label>
              <input className="form-control" type="date" value={form.periodEnd} onChange={set('periodEnd')} />
            </div>
            <button className="btn btn-primary" onClick={generate} disabled={generating} style={{ marginBottom:1 }}>
              {generating ? 'Generating…' : <><BarChart2 size={14}/> Generate QSR</>}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : reports.length ? (
            <table>
              <thead><tr><th>Customer</th><th>Period</th><th>Total</th><th>Delivered</th><th>Delivery Rate</th><th>On-Time Rate</th><th>Avg Days</th><th>RTO</th><th>Actions</th></tr></thead>
              <tbody>
                {reports.map(r=>(
                  <tr key={r.qsr_id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{r.company_name||r.customer_name}</div>
                    </td>
                    <td className="text-sm">{fmtDate(r.period_start)} – {fmtDate(r.period_end)}</td>
                    <td style={{ fontWeight:500 }}>{r.total_shipments}</td>
                    <td style={{ color:'var(--success)', fontWeight:500 }}>{r.delivered}</td>
                    <td>
                      <span style={{ fontWeight:700, color: parseFloat(r.delivery_rate)>=98?'var(--success)':parseFloat(r.delivery_rate)>=90?'var(--warning)':'var(--danger)' }}>
                        {r.delivery_rate}%
                      </span>
                    </td>
                    <td style={{ fontWeight:600 }}>{r.on_time_rate}%</td>
                    <td>{r.avg_transit_days||'—'} days</td>
                    <td style={{ color:'var(--danger)' }}>{r.rto}</td>
                    <td><button className="btn btn-outline btn-sm" onClick={()=>printQSR(r)}>🖨️ Print</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div style={{ fontSize:48, marginBottom:12 }}>📊</div>
              <h3>No QSR reports generated yet</h3>
              <p>Use the form above to generate a quality service report for any corporate customer.</p>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function WKpi({ label, v, color }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ fontSize:'1.3rem', color: color||'var(--navy)' }}>{v}</div>
    </div>
  );
}
